/**
 * TemplateSaveForm — the ONE save form (D8): category (optional) → that
 * section's content, prefilled from wherever it was opened and untick-able →
 * name → Save.
 *
 * A CENTRED MODAL, portalled to document.body: opened from the last row of a
 * (potentially 200-row) All-templates list, an inline panel would render below
 * that list and off screen — the bay-edit-form failure this build must not
 * repeat (mockup 06).
 *
 * Same doctor + same name (case-insensitive) → Replace-or-save-as-new before
 * anything is written (D9); Replace keeps the id and the usage count.
 *
 * EDIT MODE (D15) — pass `template`: the form prefills from THAT template
 * instead of the pad, saves with PUT to the same id (id + usage_count kept),
 * and shows the category read-only, because `category` is immutable after
 * create (it is not accepted on PUT at all — sending it is a 422). The D9
 * prompt then fires only if the doctor renames it onto ANOTHER template's name.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  buildTemplatePayload,
  findSameNameTemplate,
} from '../../utils/rxTemplates';
import { SURGERY_PLACEHOLDER } from '../../utils/rxMemory';
import {
  createPrescriptionTemplate,
  updatePrescriptionTemplate,
} from '../../services/api/prescriptionTemplateApi';
import TemplateMedicineComposer from './TemplateMedicineComposer';

const NO_CATEGORY = '';

// Which pad parts a category may carry — the client mirror of the server's
// exclusivity matrix, so a categorised save can never be built with another
// section's content in it.
const PARTS_BY_CATEGORY = {
  [NO_CATEGORY]: ['symptoms', 'diagnosis', 'medicines', 'tests', 'surgeries', 'chiefComplaint', 'notes', 'followUp'],
  [TEMPLATE_CATEGORIES.SYMPTOMS]: ['symptoms'],
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: ['diagnosis'],
  [TEMPLATE_CATEGORIES.MEDICINES]: ['medicines'],
  [TEMPLATE_CATEGORIES.LAB_TESTS]: ['tests'],
  [TEMPLATE_CATEGORIES.SURGERIES]: ['surgeries'],
};

const medicineLabel = (m) => [
  m.medicine || m.medicine_input,
  m.strength,
  m.form,
  m.duration,
].filter(Boolean).join(' · ');

export default function TemplateSaveForm({
  pad,
  template = null,            // present ⇒ EDIT mode (D15)
  // The Rx Templates card opens this form CATEGORY-FIRST with nothing to
  // prefill, so it must be possible to author content here: a medicine
  // composer, a symptom chip input and a test input appear. The pad never
  // passes this — there the content comes from the prescription itself.
  allowCompose = false,
  existingTemplates = [],
  initialName = '',
  initialCategory = NO_CATEGORY,
  // Why the category above is already filled in — passed by the pad when the
  // form was opened from a section's `Save…` (D18).
  categoryHint = null,
  onSaved,
  onClose,
  isDark = false,
}) {
  const editing = !!template;
  // In edit mode the template IS the source; the pad is not read at all, so a
  // doctor editing "Fever meds" from the card or from the list sees exactly
  // what is saved, never what happens to be on some prescription.
  const source = editing ? {
    medicines: template.medicines || [],
    symptoms: template.symptoms || [],
    investigations: template.investigations || [],
    labCatalogIds: template.lab_catalog_ids || [],
    advisedSurgeries: template.advised_surgeries || [],
    diagnosis: template.diagnosis || '',
    chiefComplaint: template.chief_complaint || '',
    doctorNotes: template.doctor_notes || '',
    followUpRequired: !!template.follow_up_required,
    followUpIn: template.follow_up_in || null,
  } : (pad || {});

  const [category, setCategory] = useState(
    editing ? (template.category || NO_CATEGORY) : (initialCategory || NO_CATEGORY)
  );
  const [name, setName] = useState(editing ? (template.name || '') : initialName);
  const [diagnosisText, setDiagnosisText] = useState(source.diagnosis || '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [conflict, setConflict] = useState(null); // the same-named template

  // Rows are STATE: they start as whatever the caller prefilled (the pad, or
  // the template being edited) and the composer below can add to them.
  const [padMedicines, setPadMedicines] = useState(source.medicines || []);
  const [padSymptoms, setPadSymptoms] = useState(source.symptoms || []);
  const [padTests, setPadTests] = useState(source.investigations || []);
  const [padSurgeries, setPadSurgeries] = useState(source.advisedSurgeries || []);
  const [symptomInput, setSymptomInput] = useState('');
  const [testInput, setTestInput] = useState('');
  const [surgeryInput, setSurgeryInput] = useState('');
  // Lab-catalogue tests already attached to an existing template, passed in
  // with their NAMES by the only caller that can resolve them (the card). They
  // stay untickable so an old template can be cleared; nothing here can ATTACH
  // a new one — that picker is deliberately gone (rx-r2 owner ruling).
  const labCatalogTests = source.labCatalogTests || [];
  const [offLabTests, setOffLabTests] = useState(() => new Set());

  // Unticked-by-index/name sets — everything starts ticked (prefilled).
  const [offMedicines, setOffMedicines] = useState(() => new Set());
  const [offSymptoms, setOffSymptoms] = useState(() => new Set());
  const [offTests, setOffTests] = useState(() => new Set());
  const [offSurgeries, setOffSurgeries] = useState(() => new Set());
  const [offScalars, setOffScalars] = useState(() => new Set()); // diagnosis|chiefComplaint|notes|followUp

  const allowed = PARTS_BY_CATEGORY[category] || PARTS_BY_CATEGORY[NO_CATEGORY];
  const shows = (part) => allowed.includes(part);

  const toggleIn = (setter) => (key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const parts = useMemo(() => {
    const on = (part) => shows(part) && !offScalars.has(part);
    return {
      medicines: shows('medicines') ? padMedicines.filter((_, i) => !offMedicines.has(i)) : [],
      symptoms: shows('symptoms') ? padSymptoms.filter((s) => !offSymptoms.has(s)) : [],
      investigations: shows('tests') ? padTests.filter((t) => !offTests.has(t)) : [],
      advisedSurgeries: shows('surgeries') ? padSurgeries.filter((s) => !offSurgeries.has(s)) : [],
      diagnosis: on('diagnosis') ? diagnosisText : '',
      chiefComplaint: on('chiefComplaint') ? (source.chiefComplaint || '') : '',
      doctorNotes: on('notes') ? (source.doctorNotes || '') : '',
      followUpRequired: on('followUp') ? !!source.followUpRequired : false,
      followUpIn: source.followUpIn || null,
      // Carried through ONLY from a caller that already holds them (the Rx
      // Templates card, duplicating an old template keeps its attached tests).
      // The prescription pad never passes any: Add does not advise labs (D7),
      // so nothing on the pad can put an id here in the first place.
      labCatalogIds: on('tests')
        ? (labCatalogTests.length > 0
          ? labCatalogTests.filter((t) => !offLabTests.has(t.id)).map((t) => t.id)
          : (source.labCatalogIds || []))
        : [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, offMedicines, offSymptoms, offTests, offSurgeries, offScalars, offLabTests, diagnosisText,
      padMedicines, padSymptoms, padTests, padSurgeries, pad, template]);

  const hasAnything = parts.medicines.length > 0 || parts.symptoms.length > 0
    || parts.investigations.length > 0 || parts.advisedSurgeries.length > 0
    || parts.diagnosis.trim() || parts.chiefComplaint.trim()
    || parts.doctorNotes.trim() || parts.followUpRequired || parts.labCatalogIds.length > 0;

  // Nothing on the pad for this category at all vs. everything unticked — two
  // different reasons, two different sentences.
  const padHasAnythingForCategory = allowCompose
    || (shows('medicines') && padMedicines.length > 0)
    || (shows('symptoms') && padSymptoms.length > 0)
    || (shows('tests') && padTests.length > 0)
    || (shows('surgeries') && padSurgeries.length > 0)
    || (shows('diagnosis') && !!(source.diagnosis || '').trim())
    || (shows('chiefComplaint') && !!(source.chiefComplaint || '').trim())
    || (shows('notes') && !!(source.doctorNotes || '').trim())
    || (shows('followUp') && !!source.followUpRequired);

  // Esc closes the modal (Cancel and a click on the backdrop already do).
  // Nothing is written on any of the three — the doctor's values are simply
  // dropped, same as Cancel.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const write = useCallback(async (mode, existing) => {
    setSaving(true);
    setFormError(null);
    try {
      // 'edit' → PUT to the template being edited · 'replace' → PUT to the
      // same-named one the doctor chose · 'new' → POST. `forUpdate` drops
      // `category` from the body (immutable after create — a 422 otherwise).
      const putTarget = mode === 'edit' ? template : (mode === 'replace' ? existing : null);
      const saved = putTarget
        ? await updatePrescriptionTemplate(
          putTarget.template_id,
          buildTemplatePayload({ name, category: category || null, parts }, { forUpdate: true })
        )
        : await createPrescriptionTemplate(
          buildTemplatePayload({ name, category: category || null, parts })
        );
      onSaved(saved, mode);
      onClose();
    } catch (err) {
      setConflict(null);
      setFormError(editing
        ? 'Could not save your changes. They are still here — try again.'
        : 'Could not save the template. Your prescription is untouched — try again.');
    } finally {
      setSaving(false);
    }
  }, [name, category, parts, template, editing, onSaved, onClose]);

  const handleSave = useCallback(() => {
    if (saving) return;                       // one POST per save, no double-submit
    if (!name.trim()) { setFormError('Give this template a name.'); return; }
    if (!hasAnything) {
      setFormError(padHasAnythingForCategory
        ? 'Select at least one item to save.'
        : 'Fill in at least one part of the prescription to save a template.');
      return;
    }
    // Editing: only a rename ONTO ANOTHER template's name is a collision — its
    // own name is not a conflict with itself.
    const same = findSameNameTemplate(existingTemplates, name, {
      excludeId: editing ? template.template_id : null,
    });
    if (same) { setConflict(same); return; }
    write(editing ? 'edit' : 'new');
  }, [saving, name, hasAnything, padHasAnythingForCategory, existingTemplates, editing, template, write]);

  // ── styles ──────────────────────────────────────────────────────────
  const surface = isDark ? '#0f172a' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.18)';
  const ink = isDark ? '#e2e8f0' : '#1e293b';
  const inkFaint = isDark ? '#94a3b8' : '#64748b';
  const controlBg = isDark ? '#1e293b' : '#ffffff';
  const field = {
    width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem',
    borderRadius: '0.375rem', border: `1px solid ${border}`,
    background: controlBg, color: ink, fontSize: '0.875rem', outline: 'none',
  };
  const stepLabel = {
    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: inkFaint, marginBottom: '0.25rem',
  };
  const tickRow = {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    fontSize: '0.875rem', color: ink, padding: '0.15rem 0',
    // A long medicine line wraps inside the modal; it never scrolls sideways.
    flexWrap: 'wrap', wordBreak: 'break-word',
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        data-testid="template-save-form"
        role="dialog"
        aria-label={editing ? `Edit "${template.name}"` : 'Save as a template'}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '24rem', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
          background: surface, color: ink, border: `1px solid ${border}`,
          borderRadius: '0.5rem', padding: '0.875rem',
          boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
          fontFamily: 'system-ui, sans-serif',
          display: 'flex', flexDirection: 'column', gap: '0.625rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ flex: 1, fontSize: '0.9375rem', fontWeight: 700 }}>
            {editing ? `Edit "${template.name}"` : 'Save as a template'}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0, width: '1.5rem', height: '1.5rem', lineHeight: 1,
              borderRadius: '0.375rem', cursor: 'pointer',
              border: `1px solid ${border}`, background: controlBg, color: ink,
              fontSize: '0.8125rem', fontWeight: 700,
            }}
          >✕</button>
        </div>

        {/* 1 — category. Immutable once the template exists (D15): shown, not
            editable, with what to do instead. */}
        <div>
          <div style={stepLabel}>Category</div>
          {editing ? (
            <div
              data-testid="template-category-readonly"
              style={{ ...field, background: isDark ? '#0b1220' : '#f8fafc', color: inkFaint }}
            >
              {category ? TEMPLATE_CATEGORY_LABELS[category] : 'No category — whole prescription'}
            </div>
          ) : (
            <select
              aria-label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={field}
            >
              <option value={NO_CATEGORY}>No category — whole prescription</option>
              {Object.values(TEMPLATE_CATEGORIES).map((c) => (
                <option key={c} value={c}>{TEMPLATE_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          )}
          <div style={{ fontSize: '0.75rem', color: inkFaint, marginTop: '0.2rem' }}>
            {editing
              ? 'The category cannot be changed. To move this to another category, save it as a new template and delete this one.'
              : (categoryHint || 'Optional — leave it empty to save the whole prescription.')}
          </div>
        </div>

        {/* 2 — content, prefilled from the pad */}
        <div>
          <div style={{ fontSize: '0.75rem', color: inkFaint, marginBottom: '0.3rem' }}>
            Untick anything that should not be part of this template.
            {' '}Vitals are never saved into a template.
          </div>

          {shows('symptoms') && (padSymptoms.length > 0 || allowCompose) && (
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={stepLabel}>Symptoms</div>
              {allowCompose && (
                <input
                  type="text"
                  value={symptomInput}
                  aria-label="Add a symptom"
                  onChange={(e) => setSymptomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const val = symptomInput.trim();
                    if (!val) return;
                    setPadSymptoms((prev) => (prev.includes(val) ? prev : [...prev, val]));
                    setSymptomInput('');
                  }}
                  placeholder="e.g. Fever, Cough, Sore throat"
                  style={{ ...field, marginBottom: '0.3rem' }}
                />
              )}
              {padSymptoms.map((s) => (
                <label key={s} style={tickRow}>
                  <input type="checkbox" checked={!offSymptoms.has(s)} aria-label={s}
                    onChange={() => toggleIn(setOffSymptoms)(s)} />
                  {s}
                </label>
              ))}
            </div>
          )}

          {shows('diagnosis') && (
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={stepLabel}>Diagnosis</div>
              <input type="text" aria-label="Diagnosis" value={diagnosisText}
                onChange={(e) => setDiagnosisText(e.target.value)} style={field} />
            </div>
          )}

          {shows('medicines') && (padMedicines.length > 0 || allowCompose) && (
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={stepLabel}>Medicines</div>
              {allowCompose && (
                <TemplateMedicineComposer
                  isDark={isDark}
                  onAdd={(rows) => setPadMedicines((prev) => [...prev, ...rows])}
                />
              )}
              {padMedicines.map((m, i) => (
                <label key={m._id || `${m.medicine}-${i}`} style={tickRow}>
                  <input type="checkbox" checked={!offMedicines.has(i)} aria-label={m.medicine || m.medicine_input}
                    onChange={() => toggleIn(setOffMedicines)(i)} />
                  {medicineLabel(m)}
                </label>
              ))}
            </div>
          )}

          {shows('tests') && (padTests.length > 0 || labCatalogTests.length > 0 || allowCompose) && (
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={stepLabel}>Lab tests</div>
              {allowCompose && (
                <input
                  type="text"
                  value={testInput}
                  aria-label="Add a lab test"
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const val = testInput.trim();
                    if (!val) return;
                    setPadTests((prev) => (prev.includes(val) ? prev : [...prev, val]));
                    setTestInput('');
                  }}
                  placeholder='Add a lab test, e.g. "CBC"'
                  style={{ ...field, marginBottom: '0.3rem' }}
                />
              )}
              {padTests.map((t) => (
                <label key={t} style={tickRow}>
                  <input type="checkbox" checked={!offTests.has(t)} aria-label={t}
                    onChange={() => toggleIn(setOffTests)(t)} />
                  {t}
                </label>
              ))}
              {labCatalogTests.map((t) => (
                <label key={t.id} style={tickRow}>
                  <input type="checkbox" checked={!offLabTests.has(t.id)} aria-label={t.name}
                    onChange={() => toggleIn(setOffLabTests)(t.id)} />
                  {t.name}
                </label>
              ))}
            </div>
          )}

          {shows('surgeries') && (padSurgeries.length > 0 || allowCompose) && (
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={stepLabel}>Surgeries</div>
              {allowCompose && (
                <input
                  type="text"
                  value={surgeryInput}
                  aria-label="Add a surgery"
                  onChange={(e) => setSurgeryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const val = surgeryInput.trim();
                    if (!val) return;
                    setPadSurgeries((prev) => (prev.includes(val) ? prev : [...prev, val]));
                    setSurgeryInput('');
                  }}
                  placeholder={SURGERY_PLACEHOLDER}
                  style={{ ...field, marginBottom: '0.3rem' }}
                />
              )}
              {padSurgeries.map((s) => (
                <label key={s} style={tickRow}>
                  <input type="checkbox" checked={!offSurgeries.has(s)} aria-label={s}
                    onChange={() => toggleIn(setOffSurgeries)(s)} />
                  {s}
                </label>
              ))}
            </div>
          )}

          {shows('chiefComplaint') && !!(pad?.chiefComplaint || '').trim() && (
            <label style={tickRow}>
              <input type="checkbox" checked={!offScalars.has('chiefComplaint')} aria-label="Chief complaint"
                onChange={() => toggleIn(setOffScalars)('chiefComplaint')} />
              {`Chief complaint — ${pad.chiefComplaint}`}
            </label>
          )}
          {shows('notes') && !!(pad?.doctorNotes || '').trim() && (
            <label style={tickRow}>
              <input type="checkbox" checked={!offScalars.has('notes')} aria-label="Instructions"
                onChange={() => toggleIn(setOffScalars)('notes')} />
              Instructions
            </label>
          )}
          {shows('followUp') && !!pad?.followUpRequired && (
            <label style={tickRow}>
              <input type="checkbox" checked={!offScalars.has('followUp')} aria-label="Follow-up"
                onChange={() => toggleIn(setOffScalars)('followUp')} />
              Follow-up
            </label>
          )}
        </div>

        {/* 3 — name */}
        <div>
          <div style={stepLabel}>Template name</div>
          <input type="text" aria-label="Template name" value={name} maxLength={80}
            placeholder="e.g. Viral fever"
            onChange={(e) => { setName(e.target.value); setFormError(null); }}
            style={field} />
        </div>

        {formError && (
          <div style={{ fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#b91c1c' }}>{formError}</div>
        )}

        {conflict ? (
          <div style={{
            border: `1px solid ${border}`, borderRadius: '0.375rem', padding: '0.6rem',
            background: isDark ? '#1e293b' : '#f8fafc',
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.2rem' }}>
              {`You already have a template called "${conflict.name}"`}
            </div>
            <div style={{ fontSize: '0.8125rem', color: inkFaint, marginBottom: '0.5rem' }}>
              Replace it with what is on this prescription, or keep both?
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button type="button" disabled={saving} onClick={() => write('replace', conflict)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: '0.375rem', border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer' }}>
                Replace it
              </button>
              <button type="button" disabled={saving} onClick={() => write('new')}
                style={{ padding: '0.35rem 0.7rem', borderRadius: '0.375rem', border: `1px solid ${border}`, background: controlBg, color: ink, fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                Save as new
              </button>
              <button type="button" onClick={() => setConflict(null)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: '0.375rem', border: `1px solid ${border}`, background: controlBg, color: ink, fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '0.4rem 0.8rem', borderRadius: '0.375rem', border: `1px solid ${border}`, background: controlBg, color: ink, fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} disabled={saving}
              style={{ padding: '0.4rem 0.8rem', borderRadius: '0.375rem', border: 'none', background: '#0d9488', color: '#fff', fontSize: '0.8125rem', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {editing ? 'Save changes' : 'Save template'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
